/**
 * SegmentedControl (DNKADM-0304 U8) — the approved board design's switcher
 * treatment (`docs/design/ReservationsBoard.dc.html`): ONE recessed container
 * (3px padding, 11px radius) holding borderless segments (8px radius), the
 * active segment a filled pill. Replaces the adjacent outlined-button pairs
 * the control bar used before — one container, internal segments, no
 * per-button borders.
 *
 * The active segment is unambiguous WITHOUT relying on colour alone
 * (spec item 34): the filled pill (a luminance step), the heavier weight, and
 * `aria-pressed` all carry it. Segments are ≥44px tall — the tablet-touch
 * floor the rest of the board uses. Colours are theme tokens only, so both
 * modes stay clean.
 */
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { resolveTokens } from '@/theme';

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
}

export function SegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  /** The group's accessible name (unchanged from the old ButtonGroup labels). */
  label: string;
  value: T;
  options: readonly SegmentOption<T>[];
  onChange: (value: T) => void;
}) {
  const t = resolveTokens(useTheme());
  return (
    <Box
      role="group"
      aria-label={label}
      sx={{
        display: 'flex',
        gap: '3px',
        p: '3px',
        borderRadius: '11px',
        bgcolor: t.raise,
      }}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <Box
            key={option.value}
            component="button"
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            sx={{
              font: 'inherit',
              fontSize: 14,
              fontWeight: active ? 600 : 500,
              minHeight: 44,
              px: 2,
              border: 0,
              borderRadius: '8px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              bgcolor: active ? t.brandSoft : 'transparent',
              color: active ? t.brand : t.text2,
              '&:hover': { bgcolor: active ? t.brandSoft2 : t.brandFaint },
            }}
          >
            {option.label}
          </Box>
        );
      })}
    </Box>
  );
}
